/**
 * ProductOrderPage.jsx
 *
 * Page de commande de produit — miroir fonctionnel de ListingViewPage.js
 * Route : /product/:id/order
 *
 * Comportement :
 *  1. Charge le produit via GET /products/:id
 *  2. Valide que l'ID correspond bien à un produit via isProductReservation()
 *     → Si non (c'est un listing), redirige vers /view-listing/:id
 *  3. Affiche les informations produit (images, titre, description, variantes,
 *     vendeur, localisation pickup)
 *  4. Ouvre la ProductOrderSidebar pour créer la commande
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardMedia, Button,
  Chip, Divider, Avatar, IconButton, Alert, CircularProgress,
  Paper, List, ListItem, ListItemText, ListItemIcon, Container,
  Fab, Drawer, useTheme, useMediaQuery, Rating, Stack,
  Accordion, AccordionSummary, AccordionDetails, keyframes
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  ShoppingCart as CartIcon,
  Inventory2 as VariantIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Category as CategoryIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useCategoryName } from '../../utils/categoryTranslation';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useFavorites } from '../../context/FavoritesContext';
import productService from '../../services/productService';
import isProductReservation from '../../utils/isProductReservation';
import ProductOrderSidebar from '../../components/product/ProductOrderSidebar';
import ListingDetailsView from '../../components/listing/ListingDetailsView';
import ReadOnlyMap from '../../components/common/ReadOnlyMap';
import UserReputationModal from '../../components/reputation/UserReputationModal';
import ListingReviewsList from '../../components/reviews/ListingReviewsList';
import api from '../../services/api';
import { getProductConditionTranslationKey } from '../../constants/productConditions';

// ─── Animation FAB ─────────────────────────────────────────────────────────
const pulseAnimation = keyframes`
  0%   { transform: scale(1);    box-shadow: 0 4px 20px rgba(0,0,0,0.2), 0 0 0 0   rgba(98,0,238,0.7); }
  50%  { transform: scale(1.05); box-shadow: 0 6px 25px rgba(0,0,0,0.3), 0 0 0 10px rgba(98,0,238,0.3); }
  100% { transform: scale(1);    box-shadow: 0 4px 20px rgba(0,0,0,0.2), 0 0 0 0   rgba(98,0,238,0.7); }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────
const UPLOADS_BASE_URL =
  process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const getImageUrl = (p) => {
  if (!p) return '/assets/placeholder-equipment.svg';
  if (typeof p === 'object' && p.url) return p.url;
  if (typeof p === 'string' && p.startsWith('http')) return p;
  return `${UPLOADS_BASE_URL}${String(p).startsWith('/') ? p : '/' + p}`;
};

const VARIANT_TYPE_LABELS = {
  color: 'Couleur', size: 'Taille', weight: 'Poids', material: 'Matière',
  style: 'Style', flavor: 'Parfum / Goût', format: 'Format',
  capacity: 'Capacité / Volume', edition: 'Édition', other: 'Autre'
};
const variantTypeLabel = (type) => VARIANT_TYPE_LABELS[type] || type;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return 'N/A';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch { return 'N/A'; }
};

// ─── Composant ────────────────────────────────────────────────────────────
const ProductOrderPage = () => {
  const { id }                  = useParams();
  const navigate                = useNavigate();
  const { t }                   = useTranslation(['products','common']);
  const { user }                = useAuth();
  const { showNotification }    = useNotifications();
  const { formatPrice }         = useCurrency();
  const { isFavorite, isPending, toggleFavorite } = useFavorites();
  const getCategoryName         = useCategoryName();
  const theme                   = useTheme();
  const isMobile                = useMediaQuery(theme.breakpoints.down('md'));

  // ── États ──────────────────────────────────────────────────────────────
  const [product,            setProduct           ] = useState(null);
  const [loading,            setLoading           ] = useState(true);
  const [validating,         setValidating        ] = useState(true); // validation isProductReservation
  const [selectedImage,      setSelectedImage     ] = useState(0);
  const [sidebarOpen,        setSidebarOpen       ] = useState(!isMobile);
  const [ownerStats,         setOwnerStats        ] = useState(null);
  const [ownerStatsLoading,  setOwnerStatsLoading ] = useState(false);
  const [reputationModalOpen, setReputationModalOpen] = useState(false);
  const [expandedPanels,     setExpandedPanels    ] = useState({
    description: true,
    details: true,
    variants:    true,
    availability: false,
    location:    false,
    owner:       true,
    reviews:     true
  });

  // ── Validation UUID produit (via isProductReservation util) ────────────
  useEffect(() => {
    let cancelled = false;
    const validate = async () => {
      setValidating(true);
      const isProduct = await isProductReservation(id);
      if (cancelled) return;
      if (!isProduct) {
        // Rediriger vers la page listing classique
        navigate(`/view-listing/${id}`, { replace: true });
        return;
      }
      setValidating(false);
    };
    if (id) validate();
    return () => { cancelled = true; };
  }, [id, navigate]);

  // ── Chargement du produit ─────────────────────────────────────────────
  useEffect(() => {
    if (validating) return; // Attendre fin de validation
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await productService.getById(id);
        const p = res.data || res;
        setProduct(p);
        if (p.seller?.id) fetchOwnerStats(p.seller.id);
      } catch (err) {
        showNotification('Produit introuvable.', 'error');
        navigate('/', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProduct();
  }, [id, validating]);

  // ── Statistiques vendeur ────────────────────────────────────────────────
  const fetchOwnerStats = async (ownerId) => {
    try {
      setOwnerStatsLoading(true);
      const res = await api.get(`/users/${ownerId}/reputation`);
      const data = res.data?.data;
      setOwnerStats({
        averageRating: data?.reputation?.score            || 0,
        totalReviews:  data?.reputation?.totalReviews     || 0,
        memberSince:   data?.user?.memberSince            || null
      });
    } catch {
      setOwnerStats({ averageRating: 0, totalReviews: 0, memberSince: null });
    } finally {
      setOwnerStatsLoading(false);
    }
  };

  // ── Navigation images ────────────────────────────────────────────────────
  const images = product?.images || [];

  const goToNext = () => setSelectedImage((i) => (i === images.length - 1 ? 0 : i + 1));
  const goToPrev = () => setSelectedImage((i) => (i === 0 ? images.length - 1 : i - 1));

  useEffect(() => {
    const handler = (e) => {
      if (images.length > 1) {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); goToPrev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); goToNext(); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length, selectedImage]);

  // ── Accordéons ─────────────────────────────────────────────────────────
  const togglePanel = (panel) => (_, isExp) =>
    setExpandedPanels((prev) => ({ ...prev, [panel]: isExp }));

  // ── Effet : désactiver scrollbar page mère quand sidebar ouvert ─────────
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [sidebarOpen]);

  // ── Effet : fermer sidebar si produit inactif ────────────────────────────
  useEffect(() => {
    if (product && product.status !== 'active' && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [product, sidebarOpen]);

  // ── Prix de base (min des options) ──────────────────────────────────────
  const basePriceCents = (() => {
    if (!product?.variants?.length) return (product?.price || 0) * 100;
    const prices = product.variants.flatMap((v) =>
      v.options.map((o) => parseFloat(o.price || 0)).filter((p) => p > 0)
    );
    return prices.length ? Math.min(...prices) * 100 : (product?.price || 0) * 100;
  })();
  const basePrice = basePriceCents / 100;

  const formatAmt = (amount) => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency', currency: product?.currency || 'EUR',
        minimumFractionDigits: 0, maximumFractionDigits: 2
      }).format(amount);
    } catch { return `${amount} ${product?.currency || 'EUR'}`; }
  };

  // Helper : Vérifier si tous les stocks sont épuisés
  const isAllStockExhausted = () => {
    if (!product?.variants?.length) {
      // Si pas de variantes, vérifier la quantité globale
      return (product?.quantity || 0) === 0;
    }
    // Toutes les variantes: toutes les options doivent avoir quantity = 0
    return product.variants.every((variant) =>
      variant.options.every((opt) => (parseInt(opt.quantity, 10) || 0) === 0)
    );
  };

  // ── États de chargement ─────────────────────────────────────────────────
  if (validating || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {validating ? t('order.page.verifying','Vérification du produit…') : t('order.page.loading','Chargement…')}
        </Typography>
      </Box>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{t('order.page.notFound','Produit introuvable.')}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            mr: 2,
            border: '1px solid #6200ee',
            borderColor: '#6200ee',
            color: '#6200ee',
            fontWeight: 'medium',
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: '0 2px 4px rgba(98, 0, 238, 0.1)',
            '&:hover': {
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              color: 'white',
              borderColor: 'transparent',
              boxShadow: '0 4px 8px rgba(98, 0, 238, 0.3)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          {t('order.page.back','Retour')}
        </Button>
      </Container>
    );
  }

  const isActive = product.status === 'active';
  const reviewListingId = product.twinListing?.id || product.listing_id || null;
  const productConditionLabel = product.condition
    ? t(getProductConditionTranslationKey(product.condition))
    : t('detail.conditionMissing');
  const favorited = isFavorite('product', product?.id);
  const favoritePending = isPending('product', product?.id);

  const handleFavoriteToggle = async () => {
    if (!user) {
      showNotification(t('common:search.favorite.loginRequired', 'Connectez-vous pour ajouter aux favoris'), 'info');
      return;
    }
    try {
      const result = await toggleFavorite({ type: 'product', targetId: product.id });
      if (!result?.ignored) {
        showNotification(result?.isFavorite ? t('common:search.favorite.added', 'Ajouté aux favoris') : t('common:search.favorite.removed', 'Retiré des favoris'), 'success');
      }
    } catch (error) {
      showNotification(error?.response?.data?.message || 'Impossible de mettre à jour le favori', 'error');
    }
  };

  // ─── Rendu ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 2, position: 'relative' }}>
      {/* ── Overlay flou quand sidebar ouvert ── */}
      {sidebarOpen && (
        <Box
          onClick={() => setSidebarOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 1200,
            pointerEvents: 'auto',
            cursor: 'pointer'
          }}
        />
      )}
      <Container maxWidth="xl">

        {/* ── Bouton retour ── */}
        <Box mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mr: 2,
              border: '1px solid #6200ee',
              borderColor: '#6200ee',
              color: '#6200ee',
              fontWeight: 'medium',
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(98, 0, 238, 0.1)',
              '&:hover': {
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                borderColor: 'transparent',
                boxShadow: '0 4px 8px rgba(98, 0, 238, 0.3)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('order.page.back','Retour')}
          </Button>
        </Box>

        {/* ── FAB commander (mobile / sidebar fermée) ── */}
        {isActive && !sidebarOpen && (
          <Fab
            color="primary"
            aria-label="Commander"
            onClick={() => setSidebarOpen(true)}
            sx={{
              position:  'fixed',
              top:       '50%',
              right:     24,
              transform: 'translateY(-50%)',
              zIndex:    1301,
              animation: `${pulseAnimation} 2s ease-in-out infinite`,
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)'
            }}
          >
            <CartIcon />
          </Fab>
        )}

        {/* ── Alerte si produit inactif ── */}
        {!isActive && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: theme.palette.warning.dark,
              color: 'white',
              '& .MuiAlert-icon': { color: 'white' }
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white' }}>
              {t('products:order.page.orderUnavailable','Commande indisponible')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t('products:order.page.statusPrefix', 'Ce produit est actuellement au statut')}{' '}
              <strong>{t(`products:productStatus.${product.status}`, product.status || 'inconnu')}</strong>{' '}
              {t('products:order.page.statusSuffix', 'et ne peut pas être commandé.')}
            </Typography>
          </Alert>
        )}

        {/* ── Titre & infos principales ── */}
        <Box
          sx={{
            mb: 2,
            py: 2,
            px: 3,
            borderRadius: 2,
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'}>
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                  {product.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {product.category?.name && (
                    <Chip icon={<CategoryIcon />} label={getCategoryName(product.category)} size="small" variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '& .MuiChip-icon': { color: 'white' } }} />
                  )}
                  <Chip
                    icon={<InfoIcon />}
                    label={`${t('conditions.label')}: ${productConditionLabel}`}
                    size="small"
                    variant="outlined"
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '& .MuiChip-icon': { color: 'white' } }}
                  />
                  {isActive && !isAllStockExhausted() && (
                    <Chip icon={<CheckCircleIcon />} label={t('order.page.available','Disponible')} size="small" color="success" />
                  )}
                  {isActive && isAllStockExhausted() && (
                    <Chip icon={<CheckCircleIcon />} label={t('order.page.outOfStock','Épuisé')} size="small" color="error" />
                  )}
                  {!isActive && (
                    <Chip icon={<BlockIcon />} label={t('order.page.disabled','Produit désactivé')} size="small" color="default" />
                  )}
                  {basePrice > 0 && (
                    <Chip
                      icon={<EuroIcon />}
                      label={`${t('order.page.from','À partir de')} ${formatAmt(basePrice)}`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                    />
                  )}
                </Box>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={handleFavoriteToggle} disabled={favoritePending} color={favorited ? 'error' : 'default'} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                  {favorited ? <FavoriteIcon /> : <FavoriteBorderIcon sx={{ color: 'white' }} />}
                </IconButton>
                <IconButton
                  onClick={() => {
                    navigator.clipboard?.writeText(window.location.href);
                    showNotification(t('search.share.copied', 'Lien copié !'), 'success');
                  }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
                >
                  <ShareIcon sx={{ color: 'white' }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        {/* ── Grille principale ── */}
        <Grid container spacing={3} justifyContent="center">

          {/* ── Colonne principale (images + détails) ── */}
          <Grid item xs={12} lg={sidebarOpen ? 8 : 9}>

            {/* Images */}
            <Card sx={{ mb: 3, position: 'relative' }}>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="420"
                  image={getImageUrl(images[selectedImage])}
                  alt={`${product.title} ${selectedImage + 1}`}
                  sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                />
                {images.length > 1 && (
                  <>
                    <IconButton
                      onClick={goToPrev}
                      sx={{
                        position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.5)', color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' }, zIndex: 1
                      }}
                    >
                      <ChevronLeftIcon fontSize="large" />
                    </IconButton>
                    <IconButton
                      onClick={goToNext}
                      sx={{
                        position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.5)', color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' }, zIndex: 1
                      }}
                    >
                      <ChevronRightIcon fontSize="large" />
                    </IconButton>
                    <Box sx={{
                      position: 'absolute', bottom: 16, right: 16,
                      backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                      px: 2, py: 0.5, borderRadius: 2, fontSize: '0.875rem'
                    }}>
                      {selectedImage + 1} / {images.length}
                    </Box>
                  </>
                )}
              </Box>
              {images.length > 1 && (
                <Box sx={{ p: 2, display: 'flex', gap: 1, overflow: 'auto' }}>
                  {images.map((img, idx) => (
                    <Box
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      sx={{
                        cursor: 'pointer', borderRadius: 1, overflow: 'hidden',
                        border: idx === selectedImage ? '3px solid #6200ee' : '1px solid #ddd',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${product.title} ${idx + 1}`}
                        style={{ width: 80, height: 60, objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Card>

            {/* Description */}
            <Accordion
              expanded={expandedPanels.description}
              onChange={togglePanel('description')}
              sx={{ mb: 3, boxShadow: 2, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white',
                  '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' }
                }}
              >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>📝 {t('order.page.description','Description')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {product.description || 'Aucune description disponible.'}
                </Typography>
              </AccordionDetails>
            </Accordion>

            {product.details && Object.keys(product.details).length > 0 && (
              <ListingDetailsView
                listing={{ details: product.details, offer_type: 'product' }}
                expanded={expandedPanels.details}
                onChange={togglePanel('details')}
                accordionSx={{ mb: 3 }}
              />
            )}

            {/* Variantes / Prix */}
            {product.variants?.length > 0 && (
              <Accordion
                expanded={expandedPanels.variants}
                onChange={togglePanel('variants')}
                sx={{ mb: 3, boxShadow: 2, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', color: 'white',
                    '&:hover': { background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)' }
                  }}
                >
                  <VariantIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    🎨 {t('order.page.variantsSection','Variantes & Tarifs')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  {product.variants.map((variant) => (
                    <Box key={variant.type} sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
                        {variantTypeLabel(variant.type)}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {variant.options.map((opt) => {
                          const qty = parseInt(opt.quantity, 10) || 0;
                          const isOutOfStock = qty === 0;
                          return (
                            <Paper
                              key={opt.value}
                              variant="outlined"
                              sx={{
                                p: 1.5, minWidth: 130,
                                opacity: isOutOfStock ? 0.55 : 1,
                                border: '1px solid',
                                borderColor: isOutOfStock ? 'grey.300' : 'primary.light',
                                borderRadius: 2
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {opt.value}
                              </Typography>
                              {(opt.price_cents > 0 || parseFloat(opt.price || 0) > 0) && (
                                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                                  {opt.price_cents > 0
                                    ? formatAmt(opt.price_cents / 100)
                                    : formatAmt(parseFloat(opt.price))}
                                </Typography>
                              )}
                              <Chip
                                label={isOutOfStock ? t('order.page.outOfStock','Épuisé') : `${qty} dispo.`}
                                size="small"
                                color={isOutOfStock ? 'error' : qty <= 3 ? 'warning' : 'success'}
                                sx={{ mt: 0.5, fontSize: '0.65rem' }}
                              />
                              {opt.sku && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  SKU: {opt.sku}
                                </Typography>
                              )}
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Disponibilités & créneaux */}
            <Accordion
              expanded={expandedPanels.availability}
              onChange={togglePanel('availability')}
              sx={{ mb: 3, boxShadow: 2, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #11998e 30%, #38ef7d 90%)', color: 'white',
                  '&:hover': { background: 'linear-gradient(45deg, #38ef7d 30%, #11998e 90%)' }
                }}
              >
                <ScheduleIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  📅 {t('order.page.availability','Disponibilités')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                {product.opening_hours?.length > 0 ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('order.page.slotDuration','Durée d\'un créneau')} : <strong>{product.default_slot_duration_minutes || 60} minutes</strong>
                    </Typography>
                    <Grid container spacing={1}>
                      {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((label, idx) => {
                        const dayNum = idx + 1;
                        const hours = product.opening_hours.filter((h) => h.day === dayNum);
                        const isOpen = hours.length > 0;
                        return (
                          <Grid item xs={6} sm={4} md={3} key={dayNum}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5, textAlign: 'center',
                                backgroundColor: isOpen ? 'success.50' : 'grey.100',
                                borderColor: isOpen ? 'success.light' : 'grey.300'
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isOpen ? 'success.dark' : 'text.disabled' }}>
                                {label}
                              </Typography>
                              {isOpen ? hours.map((h, i) => (
                                <Typography key={i} variant="caption" color="success.dark" sx={{ display: 'block' }}>
                                  {h.from} – {h.to}
                                </Typography>
                              )) : (
                                <Typography variant="caption" color="text.disabled">Fermé</Typography>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ) : (
                  <Alert severity="info">
                    Les horaires de pickup ne sont pas encore configurés par le vendeur.
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Localisation pickup */}
            {product.pickup_location && (
              <Accordion
                expanded={expandedPanels.location}
                onChange={togglePanel('location')}
                sx={{ mb: 3, boxShadow: 2, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white',
                    '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' }
                  }}
                >
                  <LocationIcon sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>📍 {t('order.page.pickupPoint','Point de pickup')}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                  {product.pickup_location?.address && (
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                      {product.pickup_location.address}
                    </Typography>
                  )}
                  {product.pickup_location?.lat && product.pickup_location?.lng && (
                    <ReadOnlyMap
                      latitude={product.pickup_location.lat}
                      longitude={product.pickup_location.lng}
                      address={product.pickup_location.address}
                      height={250}
                      zoom={16}
                      showAddress={false}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic', textAlign: 'center' }}>
                    ℹ️ {t('order.page.locationPrivacy','Adresse précise communiquée après confirmation de commande.')}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Avis */}
            <Accordion
              expanded={expandedPanels.reviews}
              onChange={togglePanel('reviews')}
              sx={{ mb: 3, boxShadow: 3, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white',
                  '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' }
                }}
              >
                <StarIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>⭐ {t('order.page.reviews','Avis clients')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                {reviewListingId ? (
                  <ListingReviewsList listingId={reviewListingId} initialLimit={5} />
                ) : (
                  <Alert severity="info">
                    {t('order.page.reviewsUnavailable', 'Les avis ne sont pas encore disponibles pour ce produit.')}
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* ── Colonne vendeur (droite, desktop) ── */}
          <Grid item xs={12} lg={3}>
            <Accordion
              expanded={expandedPanels.owner}
              onChange={togglePanel('owner')}
              sx={{ mb: 3, boxShadow: 3, '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden', position: 'sticky', top: 80 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white',
                  '&:hover': { background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)' }
                }}
              >
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('order.page.seller','Vendeur')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                {/* Informations principales du propriétaire */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
                    <Avatar
                      src={product.seller?.profilePicture}
                      sx={{ width: 60, height: 60, bgcolor: 'primary.main', fontSize: '1.5rem', mb: 1 }}
                    >
                      {product.seller?.name?.charAt(0) || 'V'}
                    </Avatar>
                    {ownerStats && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Rating value={ownerStats.averageRating || 0} precision={0.1} readOnly size="small" sx={{ mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">
                          {ownerStats.averageRating?.toFixed(1) || '0.0'} ({ownerStats.totalReviews || 0})
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {product.seller?.name || t('order.page.seller', 'Vendeur')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {t('order.vendor.memberSince', 'Membre depuis')} {formatDate(ownerStats?.memberSince || product.seller?.createdAt || product.seller?.created_at)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Statistiques du propriétaire */}
                {ownerStatsLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                      <Paper 
                        elevation={0}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          background: '#0b5ed7',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundImage: 'linear-gradient(45deg, #0b5ed7 0%, #0d6efd 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: 2
                          }
                        }}
                        onClick={() => setReputationModalOpen(true)}
                      >
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                          <StarIcon className="starIcon" sx={{ fontSize: 20, color: '#FFD700' }} />
                          <Typography className="reputationText" variant="body1" sx={{ fontWeight: 'medium', color: '#ffffff' }}>
                            {t('order.vendor.viewReputation', 'Voir la réputation détaillée')}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                )}

                {/* Actions */}
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<CartIcon />}
                    size="large"
                    onClick={() => setSidebarOpen(true)}
                    disabled={!isActive}
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                      }
                    }}
                  >
                    {t('order.page.orderBtn', 'Commander')}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<TrendingUpIcon />}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'primary.light',
                        color: 'white'
                      }
                    }}
                  >
                    {t('order.page.viewProfile', 'Voir le profil')}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>

        </Grid>
      </Container>

      {/* ── Sidebar de commande ─────────────────────────────────────────────── */}
      {isActive && (
        <ProductOrderSidebar
          product={product}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Modal réputation vendeur ─────────────────────────────────────────── */}
      {product.seller?.id && (
        <UserReputationModal
          open={reputationModalOpen}
          onClose={() => setReputationModalOpen(false)}
          userId={product.seller.id}
        />
      )}
    </Box>
  );
};

export default ProductOrderPage;
